<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
    import { getImageSizeUrl } from "@/utils/getImageSizeUrl";
    import RewardsCustomerStatusBasedContent from "@apps/rewards/components/RewardsCustomerStatusBasedContent.svelte";
    import RewardsDynamicCustomerIcon from "@apps/rewards/components/RewardsDynamicCustomerIcon.svelte";
    import RewardsList from "@apps/rewards/components/RewardsList/RewardsList.svelte";
    import SpendingRuleRewardDetails from "@apps/rewards/components/RewardsSpendingRules/SpendingRuleRewardDetails.svelte";
    import RewardsGeneralTheme from "@apps/rewards/components/Theme/RewardsGeneralTheme.svelte";
    import {
        DATA_GW_SELECTORS,
        ICON_SIZES,
    } from "@apps/rewards/constants/constants";
    import type RewardsAmountFormatter from "@apps/rewards/lib/RewardsAmountFormatter/RewardsAmountFormatter";
    import type RewardsCurrencyFormatter from "@apps/rewards/lib/RewardsCurrencyFormatter";
    import type RewardsProgramAccountQuery from "@apps/rewards/modules/ProgramAccount/query/RewardsProgramAccountQuery";
    import {
        REWARDS_SPENDING_RULES_REWARD_TYPES,
        REWARDS_SPENDING_RULES_TYPES,
    } from "@apps/rewards/modules/SpendingRules/constants";
    import { REWARDS_SPENDING_RULE_ICONS } from "@apps/rewards/modules/SpendingRules/icons";
    import type RewardsSpendingRulesQuery from "@apps/rewards/modules/SpendingRules/query/RewardsSpendingRulesQuery";
    import type RewardsSpendingRulesService from "@apps/rewards/modules/SpendingRules/service/RewardsSpendingRulesService";
    import {
        canRedeem,
        getMinSpendablePoints,
        getMissingPoints,
        selectFilteredSpendingRulesList,
    } from "@apps/rewards/modules/SpendingRules/utils/utils";
    import type RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
    import type { TranslationNamespaces } from "@apps/rewards/types";
    import { isItemMatchingType } from "@apps/rewards/utils/isItemMatchingType";
    import { fetchAndOpenModal } from "@apps/rewards/utils/modal/utils";
    import { normalizeLocale } from "@apps/rewards/utils/normalizeLocale/normalizeLocale";
    import type { LocalizationInfoProviderInterface } from "@lib/LocalizationInfoProvider/LocalizationInfoProviderInterface";
    import type { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
    import { ROLE } from "@modules/authentication/constants";
    import type LocationControllerInterface from "@modules/common_module/LocationController/LocationControllerInterface";
    import type { URLsBuilder } from "@modules/common_module/URLsBuilder/URLsBuilder";
    import CustomerQuery from "@modules/customer_module/queries/CustomerQuery";
    import CustomerService from "@modules/customer_module/services/CustomerService";
    import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
    import type { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import type HTMLSanitizerInterface from "@modules/money/HTMLSanitizer/HTMLSanitizerInterface";
    import { combineLatest, filter, map, of, tap } from "rxjs";
    import { onMount } from "svelte";
    import { fade } from "svelte/transition";

    import Image from "@components/Image/Image.svelte";
    import { ModalManager } from "@components/Modal/ModalManager";
    import PreviewBadge from "@components/PreviewBadge.svelte";
    import ProductContainer from "@components/ProductContainer.svelte";
    import T from "@components/T.svelte";
    import Text from "@components/Typography/Text.svelte";

    import RewardPageWidgetAuthButtons from "../../components/RewardPageWidgetAuthButtons.svelte";
    import RewardsPageCustomerBalance from "../../components/RewardsPageCustomerBalance.svelte";
    import RewardsPageWidgetCard from "../../components/RewardsPageWidgetCard.svelte";
    import RewardsPageWidgetCardLayoutSkeleton from "../../components/RewardsPageWidgetCardLayoutSkeleton.svelte";
    import RewardsPageWidgetContainer from "../../components/RewardsPageWidgetContainer.svelte";
    import RewardsPageWidgetEmptyState from "../../components/RewardsPageWidgetEmptyState.svelte";
    import RewardsPageWidgetLayout from "../../components/RewardsPageWidgetLayout.svelte";
    import RewardsPageWidgetTitle from "../../components/RewardsPageWidgetTitle.svelte";

    //Props
    export let storefrontValuesService: RewardsStorefrontValuesService;
    export let spendingRulesService: RewardsSpendingRulesService;
    export let spendingRulesQuery: RewardsSpendingRulesQuery;
    export let programAccountQuery: RewardsProgramAccountQuery;
    export let customerQuery: CustomerQuery;
    export let customerService: CustomerService;
    export let currencyFormatter: RewardsCurrencyFormatter;
    export let amountFormatter: RewardsAmountFormatter;
    export let dynamicImportModule: DynamicImportModule;
    export let modalManager: ModalManager;
    export let i18n: GwI18n;
    export let authenticationInfoProvider: AuthenticationInfoProviderInterface;
    export let localizationInfoProvider: LocalizationInfoProviderInterface;
    export let urlsBuilder: URLsBuilder;
    export let locationController: LocationControllerInterface;
    export let sanitizer: HTMLSanitizerInterface;
    export let showPointsBalance: boolean | null;
    export let redirectToCartAfterRedeem: boolean | null;
    export let isDesignMode: boolean | null;
    export let namespace: TranslationNamespaces;
    export let pointsNamespace: TranslationNamespaces;
    export let afterWidgetInject: () => void;

    const branding$ = storefrontValuesService.selectRewardsBranding();
    const rewardsSettings$ =
        storefrontValuesService.selectRewardsGeneralSettings();
    const points$ = programAccountQuery.points$;
    const isCustomer$ = authenticationInfoProvider
        .selectRole()
        .pipe(map((role) => role === ROLE.Customer));
    const storeCreditBalance$ = customerQuery.storeCreditBalance$;

    const spendingRulesList$ = selectFilteredSpendingRulesList(
        spendingRulesQuery.spendingRulesList$,
        isCustomer$
    );
    const status$ = spendingRulesQuery.status$;

    const { freeProduct } = REWARDS_SPENDING_RULES_TYPES;
    const { productDiscount } = REWARDS_SPENDING_RULES_REWARD_TYPES;

    const handleOpenModal = async ({
        isCustomer,
        canRedeemPoints,
        ruleId,
    }: {
        isCustomer: boolean;
        canRedeemPoints: boolean;
        ruleId: string;
    }): Promise<void> => {
        if (!(isCustomer || canRedeemPoints)) return;

        fetchAndOpenModal({
            modalName:
                REWARDS_WIDGET_NAMES.REWARDS_WAYS_TO_SPEND_PAGE_WIDGET_MODAL,
            modalManager,
            dynamicImportManager:
                dynamicImportModule.exports.dynamicImportManager,
            params: {
                ruleId,
                redirectToCartAfterRedeem,
            },
        });
    };

    onMount(() => {
        spendingRulesService.checkRequestStatusAndGetSpendingRules({
            locale: normalizeLocale(
                localizationInfoProvider.getLanguageIsoCode()
            ),
        });

        const isCustomerSubscription = combineLatest([
            isCustomer$,
            rewardsSettings$,
            of(showPointsBalance),
        ])
            .pipe(
                filter(
                    ([
                        isCustomer,
                        { isStoreCreditEnabled },
                        showPointsBalance,
                    ]) =>
                        isCustomer &&
                        isStoreCreditEnabled &&
                        !!showPointsBalance
                ),
                tap(() =>
                    customerService.checkRequestStatusAndGetCustomerStoreCredit()
                )
            )
            .subscribe();

        afterWidgetInject();

        return () => {
            isCustomerSubscription.unsubscribe();
        };
    });
</script>

{#if $branding$}
    <RewardsGeneralTheme rewardsBrandingGeneral={$branding$.general}>
        <div class="gw-rd-ways-to-spend-page-widget">
            <RewardsPageWidgetContainer>
                <RewardsPageWidgetLayout>
                    {#if isDesignMode}
                        <PreviewBadge />
                    {/if}

                    {#if $status$ === STATUS.INITIAL_LOADING}
                        <RewardsPageWidgetCardLayoutSkeleton />
                    {:else}
                        <RewardsPageWidgetTitle>
                            <T {namespace} translationKey="widget_title" />
                        </RewardsPageWidgetTitle>
                        <RewardsCustomerStatusBasedContent
                            isCustomerContent={$isCustomer$ &&
                                !!showPointsBalance}
                        >
                            <RewardsPageCustomerBalance
                                slot="customer-content"
                                customerBalance={$points$?.balance}
                                pointsExpiration={$points$?.expiresAt}
                                {amountFormatter}
                                {i18n}
                                {namespace}
                                {pointsNamespace}
                                storeCreditBalance={$storeCreditBalance$}
                                isStoreCreditEnabled={$rewardsSettings$?.isStoreCreditEnabled}
                            />
                        </RewardsCustomerStatusBasedContent>
                        <div
                            class="gw-rd-ways-to-spend-page-widget__list"
                            in:fade={{ duration: 300 }}
                        >
                            <RewardsList
                                items={$spendingRulesList$}
                                layout="page"
                                let:item
                            >
                                {@const minSpendablePoints =
                                    getMinSpendablePoints(item.rule)}
                                {@const missingPoints = getMissingPoints({
                                    pointsPrice: item.rule.pointsPrice,
                                    userBalance: $points$?.balance,
                                    minSpendablePoints,
                                })}
                                {@const canRedeemPoints = canRedeem({
                                    pointsPrice: item.rule.pointsPrice,
                                    userBalance: $points$?.balance,
                                    minSpendablePoints,
                                })}

                                {#if isItemMatchingType(item, freeProduct, productDiscount)}
                                    <ProductContainer
                                        productId={item.metadata.products[0].id}
                                        let:productInfo
                                    >
                                        <RewardsPageWidgetCard
                                            isCustomer={$isCustomer$}
                                            canOpenModal={canRedeemPoints}
                                            dataGwSelector={`${DATA_GW_SELECTORS.waysToSpendPageWidget}-${item.rule.ruleId}`}
                                            on:click={() =>
                                                handleOpenModal({
                                                    isCustomer: $isCustomer$,
                                                    canRedeemPoints,
                                                    ruleId: item.rule.ruleId,
                                                })}
                                        >
                                            <Image
                                                slot="icon"
                                                wrapperClassName="gw-rd-page-widget-card--free-product-rule"
                                                src={urlsBuilder.createProductImageUrl(
                                                    productInfo.product
                                                        ?.featuredImage?.url ||
                                                        "",
                                                    {
                                                        width: 120,
                                                    }
                                                )}
                                                objectFit="contain"
                                                alt={productInfo.product
                                                    ?.title || ""}
                                            />
                                            <Text
                                                slot="title"
                                                component="h2"
                                                variant="base"
                                                weight="semibold"
                                            >
                                                {item.rule.title}
                                            </Text>
                                            <Text
                                                slot="reward"
                                                component="span"
                                                variant="base"
                                                size="xs"
                                                compact
                                            >
                                                <SpendingRuleRewardDetails
                                                    isCustomerContent={!canRedeemPoints &&
                                                        $isCustomer$}
                                                    points={missingPoints}
                                                    pointsPrice={item.rule
                                                        .pointsPrice}
                                                    reward={item.rewards[0]}
                                                    {currencyFormatter}
                                                    {amountFormatter}
                                                    {sanitizer}
                                                    {i18n}
                                                    {namespace}
                                                    {pointsNamespace}
                                                />
                                            </Text>
                                            <RewardPageWidgetAuthButtons
                                                slot="auth-buttons"
                                                {locationController}
                                                {namespace}
                                                {urlsBuilder}
                                            />
                                        </RewardsPageWidgetCard>
                                    </ProductContainer>
                                {:else}
                                    <RewardsPageWidgetCard
                                        isCustomer={$isCustomer$}
                                        canOpenModal={canRedeemPoints}
                                        dataGwSelector={`${DATA_GW_SELECTORS.waysToSpendPageWidget}-${item.rule.ruleId}`}
                                        on:click={() =>
                                            handleOpenModal({
                                                isCustomer: $isCustomer$,
                                                canRedeemPoints,
                                                ruleId: item.rule.ruleId,
                                            })}
                                    >
                                        <RewardsDynamicCustomerIcon
                                            slot="icon"
                                            viewBox="0 0 32 32"
                                            customerIcon={getImageSizeUrl(
                                                item.metadata.icons,
                                                ICON_SIZES.small
                                            )}
                                            defaultIcon={REWARDS_SPENDING_RULE_ICONS[
                                                item.rule.ruleType
                                            ]}
                                        />
                                        <Text
                                            slot="title"
                                            component="h2"
                                            variant="base"
                                            weight="semibold"
                                        >
                                            {item.rule.title}
                                        </Text>
                                        <Text
                                            slot="reward"
                                            component="span"
                                            variant="base"
                                            size="xs"
                                            compact
                                        >
                                            <SpendingRuleRewardDetails
                                                isCustomerContent={!canRedeemPoints &&
                                                    $isCustomer$}
                                                points={missingPoints}
                                                pointsPrice={item.rule
                                                    .pointsPrice}
                                                reward={item.rewards[0]}
                                                {currencyFormatter}
                                                {amountFormatter}
                                                {sanitizer}
                                                {i18n}
                                                {namespace}
                                                {pointsNamespace}
                                            />
                                        </Text>
                                        <RewardPageWidgetAuthButtons
                                            slot="auth-buttons"
                                            {locationController}
                                            {namespace}
                                            {urlsBuilder}
                                        />
                                    </RewardsPageWidgetCard>
                                {/if}
                                <RewardsPageWidgetEmptyState slot="empty-state">
                                    <Text slot="content">
                                        <T
                                            {namespace}
                                            translationKey="spending_rules_empty_state"
                                        />
                                    </Text>
                                </RewardsPageWidgetEmptyState>
                            </RewardsList>
                        </div>
                    {/if}
                </RewardsPageWidgetLayout>
            </RewardsPageWidgetContainer>
        </div>
    </RewardsGeneralTheme>
{/if}

<style lang="postcss" global>
    @import "../../../../../../themes/globals.css";
    .gw-rd-ways-to-spend-page-widget {
        --gw-image-skeleton-backdrop-color: #e2e8f0;
        background-color: var(--gw-white-bg);

        &__list {
            width: 100%;
        }

        .gw-rd-page-widget-card--free-product-rule {
            width: 45px;
        }
    }
</style>
